import {
    AlertType,
    LoadingScreen,
    StyleFunction,
    Surface,
    Tabs,
    Text,
    useAuth,
    useForm,
    useMe,
    useThemedStyle,
} from "@venuepos/react-common";
import {
    GQIntegration,
    GQIntegrationQuery,
    useIntegrationLazyQuery,
    useIntegrationSaveMutation,
    useIntegrationSettingsLazyQuery,
} from "graphql-sdk";
import {
    FiskalySettings,
    IIntegrationInput,
    IntegrationType,
    schemaIntegrationInput,
} from "lib";
import React, { ReactNode, useCallback, useEffect, useState } from "react";
import { useTranslation } from "locales";
import { TextStyle, View } from "react-native";

import { useHandleMutationError } from "../../../hooks/use-handle-mutation-error";
import { RootStackScreenProps } from "../../../navigation";
import { EconomicSettingsTab } from "../economic-settings-tab";
import { GeneralForm } from "../general-form";
import { IntegrationScreen } from "../integration-screen";
import { ChartOfAccountsTab } from "./account-chart-tab";
import { CmpProductSettingsTab } from "../cmp-product-settings-tab";
import { FiskalySettingsTab } from "../fiskaly/fiskaly-settings-tab";
import { FiskalySubmissionTab } from "../fiskaly/submission/tab";
import { FiskalyOrganizationTab } from "../fiskaly/organization/tab";
import { FiskalyTaxPayerTab } from "../fiskaly/tax-payer/tab";
import { FiskalyEstablishmentTab } from "../fiskaly/establishment/tab";

type ScreenProps = RootStackScreenProps<"INTEGRATION_EDIT">;

export function EditIntegrationScreen({
    navigation: { navigate },
    route,
}: ScreenProps) {
    const auth = useAuth();
    auth.enforce("merchant.merchant_settings");

    const integrationId = route.params.id;
    const tabIndex = route.params?.tabIndex || 0;

    const me = useMe();
    const [t] = useTranslation();
    const styles = useThemedStyle(styleFunc);

    const { handleMutationError } = useHandleMutationError();
    const [integrationEdit] = useIntegrationSaveMutation();
    const [getIntegration, integration] = useIntegrationLazyQuery({
        fetchPolicy: "no-cache",
    });
    const [getIntegrationSettings, integrationSettings] =
        useIntegrationSettingsLazyQuery();
    const [addBadge, setAddBadge] = useState<boolean>(false);

    const form = useForm<IIntegrationInput>(schemaIntegrationInput, null);
    const [{ values }, { setDefaultValues }] = form;

    useEffect(() => {
        if (!integrationId) {
            navigate("INTEGRATIONS");
            return;
        }

        getIntegration({ variables: { id: integrationId } });
        getIntegrationSettings({ variables: { integrationId: integrationId } });
    }, [getIntegration, navigate, integrationId, getIntegrationSettings]);

    useEffect(() => {
        if (integration.data && integration.data.integration && me.merchant) {
            const p = integration.data.integration;
            setDefaultValues({
                type: p.type as IntegrationType,
                active: p.active,
                name: p.name,
                apiKey: p.apiKey,
                merchantId: me.merchant.id,
            });
        }
    }, [integration.data, setDefaultValues, me]);

    useEffect(() => {
        if (integrationSettings?.data?.integrationSettings) {
            const settings = integrationSettings?.data?.integrationSettings;

            if (settings.type !== "FISKALY") {
                return;
            }

            const fiskalySettings = settings as FiskalySettings;

            setAddBadge(fiskalySettings.submissionNeeded);
        }
    }, [integrationSettings?.data?.integrationSettings]);

    const edit = useCallback(async () => {
        if (
            !integration ||
            integration.loading ||
            !integration.data ||
            !integration.data.integration ||
            !values ||
            values.type === undefined
        ) {
            return;
        }

        await handleMutationError(async () => {
            await integrationEdit({
                variables: {
                    id: integration.data!.integration!.id,
                    integration: {
                        type: values.type!,
                        active: values.active,
                        name: values.name,
                        apiKey: values.apiKey,
                        merchantId: values.merchantId,
                    },
                },
            });
        }, t("common.saved", "Saved"));
    }, [integration, values, handleMutationError, t, integrationEdit]);

    const labelsAndContent = useCallback(
        (integrationType: GQIntegrationQuery["integration"]) => {
            if (!integrationType) {
                return [];
            }

            if (!integration || integration.loading || !integration.data) {
                return;
            }

            // The GeneralForm is always included
            const returnValue: {
                label: string;
                content: ReactNode;
                labelBadge?: { color: TextStyle["color"]; type: AlertType };
            }[] = [
                {
                    label: t("backoffice.common.integration", "Integration"),
                    content: (
                        <GeneralForm
                            form={form}
                            onSubmit={edit}
                            submitButton={["common.save", "Save"]}
                            pickerDisabled={true}
                            key="integration-general-form"
                        />
                    ),
                },
            ];

            switch (integrationType.type) {
                case "CMP-PRODUCT":
                    returnValue.push({
                        label: t(
                            "backoffice.common.configuration",
                            "Configuration"
                        ),
                        content: (
                            <CmpProductSettingsTab
                                integration={integrationType}
                                key="cmp-product-configuration"
                            />
                        ),
                    });
                    break;

                case "E-CONOMIC":
                    returnValue.push({
                        label: t(
                            "backoffice.common.configuration",
                            "Configuration"
                        ),
                        content: (
                            <EconomicSettingsTab
                                integration={integrationType}
                                key="economic-configuration"
                            />
                        ),
                    });

                    returnValue.push({
                        label: t(
                            "backoffice.integration.economic.account_chart",
                            "Chart of accounts"
                        ),
                        content: (
                            <ChartOfAccountsTab
                                integration={integration.data.integration}
                            />
                        ),
                    });
                    break;

                case "FISKALY":
                    returnValue.push({
                        label: t(
                            "backoffice.common.configuration",
                            "Configuration"
                        ),
                        content: (
                            <FiskalySettingsTab
                                integration={integrationType}
                                key="fiskaly-configuration"
                            />
                        ),
                    });
                    returnValue.push({
                        label: t(
                            "backoffice.integration.fiskaly.organization",
                            "Organization"
                        ),
                        content: (
                            <FiskalyOrganizationTab
                                integration={integrationType as GQIntegration}
                                key="fiskaly-organization"
                            />
                        ),
                    });
                    returnValue.push({
                        label: t(
                            "backoffice.integration.fiskaly.establishments",
                            "Establishments"
                        ),
                        content: (
                            <FiskalyEstablishmentTab
                                integration={integrationType as GQIntegration}
                                key="fiskaly-establishments"
                            />
                        ),
                    });
                    returnValue.push({
                        label: t(
                            "backoffice.integration.fiskaly.submissions",
                            "Submissions"
                        ),
                        content: (
                            <FiskalySubmissionTab
                                integration={integrationType as GQIntegration}
                                key="fiskaly-submissions"
                            />
                        ),
                        labelBadge:
                            addBadge === true
                                ? { color: "red", type: "warning" }
                                : undefined,
                    });
                    returnValue.push({
                        label: t(
                            "backoffice.integration.fiskaly.tax_payer",
                            "Tax payer"
                        ),
                        content: <FiskalyTaxPayerTab key="fiskaly-taxpayer" />,
                    });
                    break;
                default:
                    returnValue.push({
                        label: t(
                            "backoffice.common.configuration",
                            "Configuration"
                        ),
                        content: (
                            <View key="no-configuration">
                                <Text>
                                    {t(
                                        "backoffice.integration.settings.none",
                                        "There are no settings for this integration"
                                    )}
                                </Text>
                            </View>
                        ),
                    });
            }

            return returnValue;
        },
        [addBadge, edit, form, integration, t]
    );

    if (!integrationId || !integration?.data?.integration) {
        return <LoadingScreen style="light" />;
    }

    return (
        <IntegrationScreen>
            <View style={styles.container}>
                <Surface>
                    <Tabs
                        labels={labelsAndContent(
                            integration.data.integration
                        )?.map(({ label, labelBadge }) => [label, labelBadge])}
                        index={Number(tabIndex)}
                    >
                        {labelsAndContent(integration.data.integration)?.map(
                            ({ content }) => content
                        )}
                    </Tabs>
                </Surface>
            </View>
        </IntegrationScreen>
    );
}

const styleFunc: StyleFunction = () => ({
    container: {
        width: "100%",
        alignSelf: "flex-start",
    },
});
